// Single source of truth for metric display labels and aggregation policy.
// Keep this in sync with the metric_type values inserted by the data import
// pipeline (services/dynamic-schema/routes/data.js).
//
// Used by:
//   - pages/analytics/VisualItemCreator.jsx (preview)
//   - pages/analytics/SavedVisuals.jsx      (dashboard cards)
//   - pages/DashboardPage.jsx               (pinned cards)
//   - components/visuals/VisualPreview.jsx  (re-usable mini viz)
//   - components/ChartComponent.jsx         (uses COUNT_LIKE_METRICS)

export const METRIC_LABELS = {
  malaria_incidence: 'Malaria Incidence',
  flu_incidence: 'Flu Syndrome Incidence',
  flu_incidence_revised: 'Flu Syndrome (Revised)',
  pneumonia_incidence: 'Pneumonia Incidence',
  diarrhea_cases: 'Diarrheal Disease Cases',
  temperature_max: 'Maximum Temperature (Tmax)',
  temperature_min: 'Minimum Temperature (Tmin)',
  rainfall: 'Rainfall',
  relative_humidity: 'Relative Humidity (RH)',
  specific_humidity: 'Specific Humidity (SH)',
  lst_day: 'Land Surface Temp Day',
  lst_night: 'Land Surface Temp Night',
  mean_radiant_temp: 'Mean Radiant Temp (MRT)',
  utci: 'UTCI',
  cape: 'CAPE',
  surface_pressure: 'Surface Pressure (SP)',
  surface_latent_heat_flux: 'SLHF',
  surface_sensible_heat_flux: 'SSHF',
  surface_thermal_radiation: 'STRD',
  surface_solar_radiation: 'SSRD',
  spei: 'SPEI',
  zonal_wind: 'Zonal Wind (U)',
  meridional_wind: 'Meridional Wind (V)',
  vertical_velocity: 'Vertical Velocity (W)',
  pm25: 'PM2.5',
  carbon_monoxide: 'Carbon Monoxide (CO)',
  nitrogen_dioxide: 'Nitrogen Dioxide (NO2)',
  ozone: 'Ozone (O3)',
  sulfur_dioxide: 'Sulfur Dioxide (SO2)',
  formaldehyde: 'Formaldehyde (HCHO)',
  ndvi: 'NDVI',
  ndwi: 'NDWI',
  evi: 'EVI',
  chlorophyll_index: 'Chlorophyll Index (CI)',
  reci: 'RECI',
}

// Disease metrics that aggregate naturally as sums (incidence reports).
// Everything else (climate measurements, indices) aggregates as means.
export const COUNT_LIKE_METRICS = new Set([
  'diarrhea_cases',
  'flu_incidence',
  'flu_incidence_revised',
  'malaria_incidence',
  'pneumonia_incidence',
])

export const labelFor = (metricType) =>
  METRIC_LABELS[metricType] || (metricType ? metricType.replace(/_/g, ' ') : 'Value')

// The Set above is the seeded catalogue, not the universe. Any metric a user
// UPLOADS is by definition absent from it, so a membership-only test answers
// "false" for every new metric and the caller defaults it to 'mean' — which
// averages case counts across a district instead of totalling them, and looks
// entirely plausible on the map. The suffix test catches the naming convention
// the catalogue itself follows (every one of the five ends in _cases or
// _incidence), so an uploaded `cholera_cases` is treated like the
// `diarrhea_cases` sitting next to it. Anchored at the end so a RATE keeps
// averaging: `malaria_incidence_rate` ends in `rate` and is deliberately not
// matched. A count-like metric named outside this convention still falls to
// 'mean' silently — the residual gap, and why an unregistered metric should
// eventually say so in the UI rather than guess in silence.
const COUNT_LIKE_SUFFIX = /(?:^|_)(cases?|counts?|incidence|deaths?|admissions?|visits?|episodes?|positives?)$/

export const isCountLike = (metricType) => {
  if (!metricType) return false
  if (COUNT_LIKE_METRICS.has(metricType)) return true
  return COUNT_LIKE_SUFFIX.test(String(metricType).toLowerCase())
}

// Mirrors isCountLike/COUNT_LIKE_SUFFIX for the STOCK family (population and
// similar) — same registration gap, same fix (qa-spatial-gate, 2026-09):
// resolveAggregation's fallback only ever protected count-like metrics, so an
// unregistered demographic upload (or the registry API outage
// MetricRegistryContext's header calls "the worst case") silently averaged a
// population figure across a district instead of totalling it. Threading
// `category` through instead of guessing a second time does NOT close this
// gap — categoryFor's own fallback (metricCatalog.js's offlineCategoryFor)
// has no 'demographic' entries, and the registry API itself only ever reports
// category for a REGISTERED row (services/dynamic-schema/routes/registry.js:
// `category: registryRow?.category ?? null` alongside `unregistered:
// registryRow == null`) — so category is exactly as absent as aggregation in
// the one case this exists to fix. End-anchored like COUNT_LIKE_SUFFIX so
// population_density/population_growth_rate (genuine means) keep averaging.
const STOCK_LIKE_SUFFIX = /(?:^|_)(population|pop|households?|stock)$/

export const isStockLike = (metricType) => {
  if (!metricType) return false
  return STOCK_LIKE_SUFFIX.test(String(metricType).toLowerCase())
}

// A metric's registry `aggregation` decides how ChartComponent collapses
// rows sharing a bucket (month, district, …): 'sum' totals them (case
// counts), 'mean' averages them (climate indices). 'latest' is a third kind
// — a stock variable (population) where a time-average is meaningless, but
// collapsing multiple LOCATIONS in the same bucket is still additive (total
// population = sum of its sectors' populations), so it shares 'sum's literal
// accumulation, just not its display wording (never "Mean population").
export const aggregatesByLiteralSum = (aggregation) =>
  aggregation === 'sum' || aggregation === 'latest'

// The registry entry wins; the name-shape guess is only the fallback for a
// metric the registry never saw (i.e. every fresh upload). Lives here rather
// than inline in MetricRegistryContext so the rule that decides whether a
// district shows a TOTAL or an AVERAGE is a pure function with a test on it,
// instead of a one-line ternary inside a provider that no test ever calls.
// isStockLike is checked after isCountLike (mutually exclusive suffixes, but
// order still matters if that ever changes) and routes through
// defaultAggregationForCategory('demographic') rather than hardcoding
// 'latest' a second time, so the category->aggregation decision stays in the
// one place that function's own comment demands.
export const resolveAggregation = (entry, metricType) =>
  entry?.aggregation ||
  (isCountLike(metricType)
    ? 'sum'
    : isStockLike(metricType)
      ? defaultAggregationForCategory('demographic')
      : 'mean')

// The category -> default aggregation decision, in ONE place. Anything that
// registers a metric must go through this: a stock ('demographic') defaults
// to 'latest', because defaulting it to 'mean' is exactly the population bug
// this fix exists to close, and a registration UI that only ever offers
// sum/mean quietly recreates it for the next stock metric someone adds.
export const defaultAggregationForCategory = (category) => {
  if (category === 'disease') return 'sum'
  if (category === 'demographic') return 'latest'
  return 'mean'
}

export const seriesLabelPrefix = (aggregation) =>
  aggregatesByLiteralSum(aggregation) ? '' : 'Mean '
